/**
 * Cartão de emergência do grupo (rodada 3, C9).
 *
 * A tela para o pior momento: quem está junto, a quem ligar, o que a pessoa
 * não pode tomar, o seguro — e a hospedagem, para dizer ao motorista. Lê do
 * aparelho quando não há sinal (cache) e diz que está lendo do cache.
 * Telefones são links `tel:`; "Imprimir" gera a versão de papel (para a
 * mochila das crianças). Quem não preencheu aparece com "sem dados", para o
 * grupo cobrar antes de viajar.
 */

import { useOnline } from '../../sync/useOnline';
import { useTrip } from '../../trip/TripContext';
import { Icon } from '../../ui/icons';
import { Ilustracao } from '../../ui/Ilustracao';
import { Card, EmptyState, ScreenHeader, SectionLabel, Skeleton } from '../../ui/primitives';
import { FotoAvatar } from '../perfil/FotoAvatar';
import { usePessoas } from '../roteiro/people';
import { useCartoesDaViagem, type CartaoDeEmergencia } from './api';

function Telefone({ numero }: { numero: string | null }) {
  if (!numero) return null;
  return (
    <a className="emg__tel" href={`tel:${numero.replace(/[^+\d]/g, '')}`}>
      {numero}
    </a>
  );
}

function CartaoDePessoa({ c }: { c: CartaoDeEmergencia }) {
  const { fotoDe } = usePessoas();
  const { members } = useTrip();
  const foto = fotoDe(c.memberId);
  const familia = members.find((m) => m.memberId === c.memberId)?.familyName;
  const i = c.info;
  return (
    <Card className={i ? 'emg__card' : 'emg__card emg__card--sem'} data-testid="emg-card">
      <div className="emg__pessoa">
        <FotoAvatar nome={c.name} url={i ? foto.url : null} cor={i ? foto.cor : undefined} tamanho={40} />
        <span className="mapa__pessoa-texto">
          <span className="mapa__pessoa-nome">{c.me ? `${c.name} (você)` : c.name}</span>
          {i && familia && <span className="mapa__pessoa-familia">família {familia}</span>}
          {!i && <span className="emg__sem">Sem dados de emergência ainda{c.me ? ' — preencha no Perfil' : ''}</span>}
        </span>
        {/* Refino final: ligar é um botão verde de 44, um toque. */}
        {i?.phone && (
          <a className="emg__tel emg__ligar" href={`tel:${i.phone.replace(/[^+\d]/g, '')}`} aria-label={`Ligar para ${c.name}: ${i.phone}`}>
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 4h3.5l1.5 4-2 1.5a11 11 0 0 0 6.5 6.5l1.5-2 4 1.5V19a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2Z" />
            </svg>
          </a>
        )}
      </div>
      {i && (
        <dl className="emg__dados">
          {(i.contactName || i.contactPhone) && (
            <>
              <dt>Ligar para</dt>
              <dd>
                {i.contactName}
                {i.contactName && i.contactPhone ? ' · ' : ''}
                <Telefone numero={i.contactPhone} />
              </dd>
            </>
          )}
          {i.healthNotes && (
            <>
              <dt>Saúde</dt>
              <dd className="emg__saude">{i.healthNotes}</dd>
            </>
          )}
          {(i.insuranceName || i.insurancePolicy || i.insurancePhone) && (
            <>
              <dt>Seguro</dt>
              <dd>
                {[i.insuranceName, i.insurancePolicy && `apólice ${i.insurancePolicy}`].filter(Boolean).join(' · ')}
                {i.insurancePhone && (
                  <>
                    {' · '}
                    <Telefone numero={i.insurancePhone} />
                  </>
                )}
              </dd>
            </>
          )}
        </dl>
      )}
    </Card>
  );
}

export function EmergenciaScreen() {
  const { trip, tripId } = useTrip();
  const online = useOnline();
  const q = useCartoesDaViagem(tripId);

  return (
    <section className="screen emg" data-testid="emergencia">
      <ScreenHeader
        title="Cartão de emergência"
        className="emg__cab"
        leading={<Ilustracao nome="emergencia" tamanho={56} />}
        sub={!online ? <span className="emg__offline nv-pop">Sem rede · lendo do aparelho</span> : q.data?.doCache ? <span className="emg__offline nv-pop">Última cópia guardada no aparelho</span> : undefined}
        trailing={
          <button type="button" className="nv-btn nv-btn--chip emg__imprimir" onClick={() => window.print()} data-testid="emg-imprimir">
            <Icon name="documento" size={16} /> Imprimir
          </button>
        }
      />

      {/* Onde estamos primeiro, em bloco escuro: é o que se mostra ao motorista. */}
      {(trip.accommodationName || trip.accommodationAddress) && (
        <div className="emg__onde" data-testid="emg-hospedagem">
          <span className="emg__onde-icone" aria-hidden="true"><Icon name="hospedagem" size={20} /></span>
          <span className="emg__onde-texto">
            <span className="emg__onde-rotulo">Onde estamos</span>
            <span className="emg__onde-nome">{trip.accommodationName ?? 'Hospedagem'}</span>
            {trip.accommodationAddress && <span className="emg__onde-endereco">{trip.accommodationAddress}</span>}
          </span>
        </div>
      )}

      <SectionLabel>{q.data ? `Pessoas · ${q.data.cards.length}` : 'Pessoas'}</SectionLabel>
      {q.isLoading && <Skeleton height={120} count={3} />}
      {q.isSuccess && q.data === null && <EmptyState ilustracao="emergencia" title="Sem cópia no aparelho" description="Abra esta tela uma vez com internet: ela fica guardada para quando não houver sinal." />}
      {q.isSuccess && q.data && q.data.cards.map((c) => <CartaoDePessoa key={c.memberId} c={c} />)}

      <p className="auth-note emg__nota">Cada pessoa preenche o próprio cartão no Perfil. Só quem participa desta viagem vê esta tela.</p>
    </section>
  );
}
